import { Injectable } from '@angular/core';
import { Sample } from 'src/app/shared/models/sample.model';
import { Unit } from 'src/app/shared/models/specification/units.model';
import { CurrentOrder } from 'src/app/shared/models/current-order.model';
import { ParameterType } from 'src/app/shared/models/specification/parameter-type.model';
import { ParameterListValue } from 'src/app/shared/models/specification/parameter-list-value.model';
import { Parameter, TestParameter } from 'src/app/shared/models/specification/test-parameter.model';
import { OrderDataService } from 'src/app/shared/data-service/order-data.service';
import { SpecificationRequest, ISpecificationDetails } from 'src/app/shared/models/specification/specification-request.model';
import { SpecificationType } from 'src/app/shared/models/specification/specification-type.model';
import { OrderCommandService } from 'src/app/shared/data-service/order-command.service';
import { SpecificationResponse } from 'src/app/shared/models/specification/specification-response.model';
import { SpecificationViewModel } from 'src/app/shared/models/specification/specification-view-model.model';
import { ParameterSpecification } from 'src/app/shared/models/specification/prarameter-specification.model';
import { TestParameterRequest } from 'src/app/shared/models/specification/test-parameter-request.model';
import { Analysis } from 'projects/dop-ordering/src/lib/models/analysis.model';
import { Dictionary } from 'projects/dop-core/src/public-api';
import { AppConfigService } from 'src/app/core/app-config/app-config.service';
import { Specification } from 'src/app/shared/models/specification/specification.model';
import { HelperService } from 'src/app/shared/services/helper.service';
import { AnalysisType } from 'projects/dop-ordering/src/public-api';
import { DeleteSpecificationRequest } from 'src/app/shared/models/specification/delete-specification-request.model';
import { SpecificationMultipleSampleRequest } from 'src/app/shared/models/specification/specification-multipleSample-request.model';

@Injectable({
    providedIn: 'root'
})
export class ParametersService {

    constructor(
        private orderDataService: OrderDataService,
        private orderCommandService: OrderCommandService,
        private appConfigService: AppConfigService,
        private helperService: HelperService) { }

    async getParameterDetails(sample: Sample, currentOrder: CurrentOrder) {
        let testDetails: Unit[] = [];

        testDetails = await this.getTestDetails(sample.analyses, currentOrder);

        if (!currentOrder.testParameterDictionary) {
            currentOrder.testParameterDictionary = new Dictionary<TestParameter[]>();
        }

        sample.testParameters =
            await this.getTestParameters(testDetails, currentOrder.testParameterDictionary);
    }

    async getTestDetails(analyses: Analysis[], currentOrder: CurrentOrder) {
        const testCodes: string[] = [];
        const scTestCodes: string[] = [];
        const testDetails: Unit[] = [];

        const monographTypes: AnalysisType[] = [AnalysisType.MonographPackage, AnalysisType.MonographTest];
        analyses = analyses.filter(t => !monographTypes.includes(t.analysisType));
        if (analyses && analyses.length > 0) {
            for (const analysis of analyses) {
                if (analysis) {
                    if (analysis.eurodatKey && analysis.testCode) {
                        testCodes.push(analysis.eurodatKey);
                    } else {
                        if (analysis.testCode && !analysis.packageCode) {
                            scTestCodes.push(analysis.testCode);
                        }
                    }
                }
            }

            const packageCodes: string[] = [];
            const packageSCCodes: string[] = [];
            for (const analysis of analyses) {
                if (analysis.eurodatKey && analysis.packageCode) {
                    packageCodes.push(analysis.eurodatKey);
                } else {
                    if (!analysis.eurodatKey && analysis.packageCode) {
                        packageSCCodes.push(analysis.packageCode);
                    }
                }
            }
            const customerPackageCodes = analyses
                .filter(t => t?.subgroupCode && t?.isCustomerPackage).map(t => t.subgroupCode);

            const customerPackages = currentOrder.quotedItems?.quotationCommercialItem?.customerPackages?.
                filter(t => customerPackageCodes.indexOf(t?.comlimsCode) > -1);

            const customerPackagePackageCodes = [];
            customerPackages?.forEach(t => {
                customerPackagePackageCodes.push(...t.packageQuotationLines.map(q => q.code));
                testCodes.push(...t.testQuotationLines.map(q => q.code));
            });

            for (const packageCode of customerPackagePackageCodes.concat(packageCodes)) {
                const packageTests = await this.getTestCodesForPackage(currentOrder.account.code, packageCode);
                packageTests?.forEach((t: any) => {
                    testDetails.push({ code: t.code, name: t.localisedName });
                });
            }

            for (const code of testCodes) {
                const quotatableItem = await this.orderDataService.getTestQuotedItems(currentOrder.account.code, code).toPromise();
                testDetails.push({ code: quotatableItem?.testCode, name: quotatableItem?.localisedName });
            }

            for (const scTest of scTestCodes) {
                const test = await this.orderDataService.getTestDetails(scTest).toPromise();
                testDetails.push({ code: test.code, name: test.name });
            }

            for (const scPackage of packageSCCodes) {
                const test = await this.orderDataService.getPackageDetails(scPackage).toPromise();

                test?.tests?.forEach(t => {
                    testDetails.push({ code: t.code, name: t.name });
                });
            }

        }
        return testDetails;
    }

    private getParameterLOVValues(parameters: Parameter[]): Promise<any> {
        const request: TestParameterRequest = {
            codes: parameters.map(x => x.code)
        };
        return new Promise(resolve => {
            this.orderDataService.getParameterListValue(request).subscribe(data => {
                resolve(data);
            });
        });
    }

    private mapLOVValuesToParameters(parameters: Parameter[], lovData: ParameterListValue[]) {
        parameters.forEach(p => {
            const lovValues = lovData.length > 0 ? lovData.filter((k: ParameterListValue) => k.code === p.code)[0] : null;
            const defaultLov = lovValues ?
                lovValues.parameterPreconfiguredValues.filter((o: any) => o.translatedName === p.defaultValue)[0] : null;
            p.listValues = lovValues ? lovValues.parameterPreconfiguredValues : null;
            p.selectedLOV = defaultLov;
        });
    }

    private mapToParameter(data: any, testDetails: Unit[]): TestParameter[] {
        const testParameters: TestParameter[] = [];

        const testCodes = new Set<string>(testDetails.map(t => t.code));
        testCodes.forEach(t => {
            const parameters = data.filter((x: any) => x.testCode === t);
            if (parameters && parameters.length > 0) {
                testParameters.push({
                    testCode: t,
                    testName: testDetails.filter(p => p.code === t).shift()?.name,
                    parameters
                });
            }
        });

        return testParameters;
    }

    //#region Service Calls
    private async getTestCodesForPackage(accountCode: string, packageCode: string) {
        const data = await this.orderDataService.getPackageQuotedItems(accountCode, packageCode).toPromise();
        return data.tests;
    }

    private async getTestParameters(testDetails: Unit[], dictionary: Dictionary<TestParameter[]>): Promise<any> {
        return new Promise(resolve => {
            const extraTests: Unit[] = [];
            const existingTestParameters: TestParameter[] = [];
            testDetails.forEach(x => {
                if (!dictionary.containsKey(x.code)) {
                    extraTests.push(x);
                } else {
                    existingTestParameters.push(...dictionary.get(x.code));
                }
            });
            if (extraTests.length > 0) {
                this.orderDataService.getTestParameters(extraTests.map(t => t.code)).subscribe(async data => {
                    const newTestParameters = this.mapToParameter(data, testDetails);
                    const lovParameters = ([] as Parameter[]).concat(...newTestParameters.map(x => x.parameters))
                        .filter(p => p.parameterType === ParameterType.LOV);
                    if (lovParameters && lovParameters.length > 0) {
                        const lovData = await this.getParameterLOVValues(lovParameters);
                        this.mapLOVValuesToParameters(lovParameters, lovData);
                    }
                    extraTests.forEach(t => {
                        const testParameters = newTestParameters.filter(x => x.testCode === t.code);
                        dictionary.add(t.code, testParameters);
                    });
                    existingTestParameters.push(...newTestParameters);
                    resolve(existingTestParameters);
                });
            } else {
                resolve(existingTestParameters);
            }
        });
    }
    //#endregion Service Calls

    //#region Save specifications
    async saveSpecificationsForSample(sample: Sample, specifications: SpecificationViewModel[]){
        await this.saveSpecificationsForMultipleSample(sample, specifications, false, [sample]);
    }
    async saveSpecificationsForMultipleSample(sample: Sample, specifications: SpecificationViewModel[], isMultipleSample: boolean, selectedSamples: Sample[]) {
        let selectedSampleCodes = selectedSamples.map(sample => sample.sampleCode);
        const specificationRequests: SpecificationRequest[] = [];
        const specificationIdsForDelete: string[] = [];
        const specDetails: ISpecificationDetails = {
            specificationIdsForDelete,
            specifications: specificationRequests,
        };
        specifications.forEach(t => {
            let filteredParameters = t.parameters;
            if (isMultipleSample) {
                filteredParameters = t.parameters.filter(p => p.selected);
            }
            filteredParameters.forEach(p => { 
                let planningLineIds: number[] = [];
                p.analyses.forEach(analysis => {
                    planningLineIds = [...planningLineIds.concat(sample.planningLines
                        .filter(a => a.testCode === analysis.code).map(b => Number(b.id)))];
                });
                let specificationsResponses: SpecificationResponse[] = [];
                if (sample.specificationResponses && sample.specificationResponses
                    .findIndex(c => p.parameterCode === c.parameterCode) >= 0) {
                    specificationsResponses = sample.specificationResponses
                        .filter(x => x.parameterCode === p.parameterCode);
                }
                specDetails.planningLineIds = planningLineIds;
                specDetails.parameterCode = p.parameterCode;
                specDetails.parameterName = p.parameterName;

                this.addSpecifications(p, specificationsResponses, specDetails);
            });
        });

        if (specificationRequests.length > 0) {
            let result: any;
            if(isMultipleSample){
                const specifications: SpecificationMultipleSampleRequest = {
                    sampleCodes: selectedSampleCodes.map(code => Number(code)),
                    specificationViewModel: specificationRequests
                };
                result = await this.orderCommandService
                .saveSpecificationsMultipleSamples(specifications).toPromise();
            }
            else{
                result = await this.orderCommandService
                .saveSpecifications(sample.sampleCode, specificationRequests).toPromise();
            }
            result.forEach(t => {
                    selectedSamples.forEach(selectedSample => {
                        if(isMultipleSample){
                            if (selectedSample.specificationResponses.findIndex(p => p.specificationId === t.specificationId && selectedSample.sampleCode === t.sampleID) >= 0) {
                                const index = selectedSample.specificationResponses.findIndex(p => p.specificationId === t.specificationId && selectedSample.sampleCode === t.sampleID);
                                selectedSample.specificationResponses[index] = t;
                            } else {
                                if (Number(selectedSample.sampleCode) === t.sampleId) {
                                    selectedSample.specificationResponses.push(t);
                                }
                            }
                        }
                        else{
                            if (selectedSample.specificationResponses.findIndex(p => p.specificationId === t.specificationId) >= 0) {
                                const index = sample.specificationResponses.findIndex(p => p.specificationId === t.specificationId);
                                sample.specificationResponses[index] = t;
                            } else {
                                sample.specificationResponses.push(t);
                            }
                        }
                    });

            });
        }
        if (specificationIdsForDelete.length > 0) {
            if(isMultipleSample){
                const deleteSpecificationRequest: DeleteSpecificationRequest = {
                    SpecificationIds: specificationIdsForDelete,
                    SampleIds: selectedSampleCodes.map(code => Number(code)),
                };
                await this.orderCommandService.deleteSpecificationsMultipleSamples(deleteSpecificationRequest).toPromise();
            }
            else{
                await this.orderCommandService.deleteSpecifications(specificationIdsForDelete).toPromise();
            }
            specificationIdsForDelete.forEach(t => {
                const index = sample.specificationResponses.findIndex(p => p.specificationId === t);
                const parameterCode = sample.specificationResponses[index].parameterCode;
                selectedSamples.forEach(selectedSamples=>{
                    const index = selectedSamples.specificationResponses.findIndex(p => p.parameterCode === parameterCode);
                    if (index >= 0) {
                        selectedSamples.specificationResponses.splice(index, 1);
                    }
                    
                })
                if (index >= 0) {
                    sample.specificationResponses.splice(index, 1);
                }
            });
        }
    }

    addSpecifications(
        parameter: ParameterSpecification,
        specificationResponses: SpecificationResponse[],
        specDetails: ISpecificationDetails) {

        const specResponse = this.getSpecificationResponses(specificationResponses);
        if (parameter.standardSpecification?.isUpdated) {
            specDetails.specificationResponse = specResponse.standardResponse;
            specDetails.specification = parameter.parameterType === ParameterType.Date
                ? this.updateDateValue(parameter.standardSpecification) : parameter.standardSpecification;
            specDetails.specificationType = SpecificationType.Standard;
            this.insertOrDeleteSpecification(specDetails);
        }
        if (parameter.servingSpecification?.isUpdated) {
            specDetails.specificationResponse = specResponse.servingResponse;
            specDetails.specification = parameter.parameterType === ParameterType.Date
                ? this.updateDateValue(parameter.servingSpecification) : parameter.servingSpecification;
            specDetails.specificationType = SpecificationType.ServingPortion;
            this.insertOrDeleteSpecification(specDetails);
        }
        if (parameter.dailyValueSpecification?.isUpdated) {
            specDetails.specificationResponse = specResponse.dailyResponse;
            specDetails.specification = parameter.parameterType === ParameterType.Date
                ? this.updateDateValue(parameter.dailyValueSpecification) : parameter.dailyValueSpecification;
            specDetails.specificationType = SpecificationType.DailyValue;
            this.insertOrDeleteSpecification(specDetails);
        }
        if (parameter.labelClaimSpecification?.isUpdated) {
            specDetails.specificationResponse = specResponse.labelClaimResponse;
            specDetails.specification = parameter.parameterType === ParameterType.Date
                ? this.updateDateValue(parameter.labelClaimSpecification) : parameter.labelClaimSpecification;
            specDetails.specificationType = SpecificationType.LabelClaim;
            this.insertOrDeleteSpecification(specDetails);
        }
    }

    insertOrDeleteSpecification(specDetails: ISpecificationDetails) {
        if (specDetails.specification.hasValue) {
            this.insertSpecification(specDetails);
        } else {
            if (specDetails.specificationResponse && specDetails.specificationResponse.specificationId) {
                specDetails.specificationIdsForDelete.push(specDetails.specificationResponse.specificationId);
            }
        }
    }

    getSpecificationResponses(specificationResponses: SpecificationResponse[]): any {
        const res: any = {
            standardResponse: specificationResponses.filter(p => p.specificationTypeId === SpecificationType.Standard).shift(),

            servingResponse: specificationResponses.filter(p => p.specificationTypeId === SpecificationType.ServingPortion).shift(),

            dailyResponse: specificationResponses.filter(p => p.specificationTypeId === SpecificationType.DailyValue).shift(),

            labelClaimResponse: specificationResponses.filter(p => p.specificationTypeId === SpecificationType.LabelClaim).shift(),
        };
        return res;
    }

    insertSpecification(specDetails: ISpecificationDetails): void {
        const specificationRequest: SpecificationRequest = {
            parameterCode: specDetails.parameterCode,
            parameterName: specDetails.parameterName,
            expectedValue: specDetails.specification?.target,
            lowerLimit: specDetails.specification?.min,
            upperLimit: specDetails.specification?.max,
            planningLineCodes: specDetails.planningLineIds as unknown as string[],
            specificationTypeId: specDetails.specificationType,
            specificationUnit: null,
        };
        if (specDetails.specificationResponse && specDetails.specificationType === specDetails.specificationResponse.specificationTypeId) {
            specificationRequest.specificationCode = specDetails.specificationResponse?.specificationId;
        }
        // no unit code = 22 and name = 'nounit'
        const numeratorUnit: Unit = specDetails.specification.numeratorCode?.code ?
            {
                code: specDetails.specification.numeratorCode?.code,
                name: specDetails.specification.numeratorCode?.name
            } : { code: this.appConfigService.configuration?.noUnitCode, name: this.appConfigService.configuration?.noUnitName };
        const denominatorUnit: Unit = specDetails.specification.denominatorCode?.code ?
            {
                code: specDetails.specification.denominatorCode?.code,
                name: specDetails.specification.denominatorCode?.name
            } : { code: this.appConfigService.configuration?.noUnitCode, name: this.appConfigService.configuration?.noUnitName };
        specificationRequest.specificationUnit = {
            id: specDetails.specificationResponse?.specificationUnitId,
            numeratorUnitName: numeratorUnit.name,
            numeratorUnitCode: numeratorUnit.code,
            denominatorUnitName: denominatorUnit.name,
            denominatorUnitCode: denominatorUnit.code,
        };
        specDetails.specifications.push(specificationRequest);
    }

    private updateDateValue(source: Specification): Specification {
        source.target = this.helperService.formatAdditionalFieldValue('', source.target, 'DTO');
        return source;
    }

    //#endregion Save specifications
}
